import React, { useState } from 'react';
import { Modal, Button, Upload, message, Table, Typography, Space, Alert, Spin, Row, Col, Descriptions, Form, Checkbox, Select, Divider } from 'antd'; // Removed DatePicker
import { UploadOutlined, BugOutlined } from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import MomentDatePicker from './MomentDatePicker.tsx'; // Import MomentDatePicker
import LabImportDebugger from './LabImportDebugger'; // Import debugger component

const { Text, Title } = Typography;
// const { RangePicker } = DatePicker; // Removed as DatePicker is replaced

const LabPdfImportModal = ({ patientId, visible, onClose }) => {
  const [labCollectionDate, setLabCollectionDate] = useState(moment());
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedLabData, setParsedLabData] = useState([]);
  const [selectedTests, setSelectedTests] = useState(new Set()); // Track selected test unique IDs
  const [extractedDemographics, setExtractedDemographics] = useState(null);
  const [selectedPatientDemographics, setSelectedPatientDemographics] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reviewStep, setReviewStep] = useState(false);
  const [pageSize, setPageSize] = useState(10); // Add page size state
  const [showDebugger, setShowDebugger] = useState(false); // Add debugger state

  // Generate unique ID for each lab test record using more stable identifiers
  const generateTestId = (record) => {
    // Create a more stable ID based on test properties rather than array index
    const name = record.name || 'unknown';
    const originalName = record.structuredValue?.originalPdfTestName || 'unnamed';
    const value = record.structuredValue?.value || '';
    const unit = record.structuredValue?.unit || '';

    // Create a hash-like ID from the record properties
    return `${name}_${originalName}_${value}_${unit}`.replace(/[^a-zA-Z0-9_]/g, '_');
  };

  const handleFileChange = (info) => {
    if (info.fileList.length > 0) {
      setSelectedFile(info.fileList[0].originFileObj);
    } else {
      setSelectedFile(null);
    }
  };

  const handleProcessPdf = async () => {
    if (!selectedFile) {
      message.error('Please select a PDF file to upload.');
      return;
    }
    if (!labCollectionDate) {
      message.error('Please select a lab collection date.');
      return;
    }

    // Enhanced validation and debugging
    if (!patientId) {
      message.error('Patient ID is missing. Please refresh the page and try again.');
      return;
    }

    const formData = new FormData();
    formData.append('labPdf', selectedFile);
    formData.append('labCollectionDate', labCollectionDate.toISOString());

    setIsLoading(true);
    setError(null);
    setReviewStep(false);

    try {
      console.log('Processing PDF for patient:', patientId);
      console.log('File:', selectedFile.name, 'Size:', selectedFile.size);
      console.log('Collection date:', labCollectionDate.toISOString());

      const response = await axios.post(`/labs/process-pdf/${patientId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 30000, // 30 second timeout
      });

      console.log('PDF processing response:', response.data);

      const labData = response.data.data.parsedLabData || [];
      setParsedLabData(labData);
      // Initialize all tests as selected by default using unique IDs
      setSelectedTests(new Set(labData.map((record) => generateTestId(record))));
      setExtractedDemographics(response.data.data.extractedDemographics || null);
      setSelectedPatientDemographics(response.data.data.selectedPatientDemographics || null);
      setReviewStep(true);
      message.success('PDF processed successfully. Please review the data.');
    } catch (err) {
      console.error("Error processing PDF:", err);

      // Enhanced error handling
      let errorMessage = 'Failed to process PDF.';
      if (err.response) {
        errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
        console.error('Server response:', err.response.data);
      } else if (err.request) {
        errorMessage = 'Cannot connect to server. Please check if the backend is running.';
        console.error('Network error:', err.request);
      } else {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setParsedLabData([]);
      setSelectedTests(new Set());
      setExtractedDemographics(null);
      setSelectedPatientDemographics(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveImportedLabs = async () => {
    const selectedLabData = parsedLabData.filter((record) =>
      selectedTests.has(generateTestId(record))
    );

    if (!selectedLabData.length) {
      message.error('Please select at least one test to import.');
      return;
    }

    // Enhanced validation
    if (!patientId) {
      message.error('Patient ID is missing. Please refresh the page and try again.');
      return;
    }

    // Log import attempt
    console.log(`Importing ${selectedLabData.length} lab tests for patient ${patientId}`);
    console.log('Selected lab data:', selectedLabData);

    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.post(`/labs/import-reviewed-labs/${patientId}`, {
        reviewedLabData: selectedLabData,
        confirmedLabCollectionDate: labCollectionDate.toISOString(),
      }, {
        timeout: 30000 // 30 second timeout
      });

      // Log successful import
      console.log('Lab import successful:', response.data);

      message.success(`${selectedLabData.length} lab test(s) imported successfully!`);
      onClose(true, labCollectionDate.toISOString()); // Pass success and collection date for date range adjustment
      resetModalState();
    } catch (err) {
      console.error("Error importing reviewed labs:", err);

      // Enhanced error handling
      let errorMessage = 'Failed to import lab data.';
      if (err.response) {
        errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
        console.error('Server response:', err.response.data);
      } else if (err.request) {
        errorMessage = 'Cannot connect to server. Please check if the backend is running.';
        console.error('Network error:', err.request);
      } else {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const resetModalState = () => {
    setLabCollectionDate(moment());
    setSelectedFile(null);
    setParsedLabData([]);
    setSelectedTests(new Set());
    setExtractedDemographics(null);
    setSelectedPatientDemographics(null);
    setIsLoading(false);
    setError(null);
    setReviewStep(false);
  };

  const handleCancel = () => {
    resetModalState();
    onClose(false);
  };

  // Selection handlers
  const handleTestSelection = (record, checked) => {
    const testId = generateTestId(record);
    const newSelectedTests = new Set(selectedTests);
    if (checked) {
      newSelectedTests.add(testId);
    } else {
      newSelectedTests.delete(testId);
    }
    setSelectedTests(newSelectedTests);
  };

  // Select/Deselect all items across all pages
  const handleSelectAllGlobal = (checked) => {
    if (checked) {
      setSelectedTests(new Set(parsedLabData.map((record) => generateTestId(record))));
    } else {
      setSelectedTests(new Set());
    }
  };



  const isAllSelected = parsedLabData.length > 0 && selectedTests.size === parsedLabData.length;
  const isIndeterminate = selectedTests.size > 0 && selectedTests.size < parsedLabData.length;

  const reviewTableColumns = [
    {
      title: (
        <Checkbox
          indeterminate={isIndeterminate}
          checked={isAllSelected}
          onChange={(e) => handleSelectAllGlobal(e.target.checked)}
        >
          Select
        </Checkbox>
      ),
      dataIndex: 'select',
      key: 'select',
      width: 80,
      render: (_, record, index) => (
        <Checkbox
          checked={selectedTests.has(generateTestId(record))}
          onChange={(e) => handleTestSelection(record, e.target.checked)}
        />
      ),
    },
    { title: 'PDF Test Name', dataIndex: ['structuredValue', 'originalPdfTestName'], key: 'originalPdfTestName', width: 200 },
    { title: 'Mapped Name', dataIndex: 'name', key: 'name', width: 150 },
    { title: 'Value (PDF)', dataIndex: ['structuredValue', 'originalPdfResult'], key: 'originalPdfResult', width: 100 },
    { title: 'Unit (PDF)', dataIndex: ['structuredValue', 'originalPdfUnit'], key: 'originalPdfUnit', width: 100 },
    { title: 'Ref Range (PDF)', dataIndex: ['structuredValue', 'originalPdfRefRange'], key: 'originalPdfRefRange', width: 150 },
    { title: 'Converted Value', dataIndex: 'value', key: 'value', width: 100 },
    { title: 'System Unit', dataIndex: 'units', key: 'units', width: 100 },
    { title: 'Category', dataIndex: 'category', key: 'category', width: 120 },
    // TODO: Add editable cells for correction in a future iteration
  ];
  
  // Basic name parser for display (can be enhanced)
  const formatName = (last, first, middle) => {
    return `${last || ''}${last && (first || middle) ? ', ' : ''}${first || ''}${middle ? ' ' + middle : ''}`.trim() || 'N/A';
  }

  return (
    <Modal
      title="Import Lab Results from PDF"
      open={visible}
      onCancel={handleCancel}
      footer={null} // Custom footer below
      width={reviewStep ? 1200 : 600}
      destroyOnClose
    >
      <Spin spinning={isLoading}>
        {!reviewStep ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Form.Item label="Lab Collection Date" required>
              <MomentDatePicker 
                value={labCollectionDate} 
                onChange={setLabCollectionDate} 
                style={{ width: '100%' }}
                showTime={{ format: 'HH:mm' }} // Control time format
                format="YYYY-MM-DD HH:mm"   // Overall display format
              />
            </Form.Item>
            <Upload
              beforeUpload={() => false} // Prevent auto-upload
              onChange={handleFileChange}
              maxCount={1}
              accept=".pdf"
              fileList={selectedFile ? [ { uid: '-1', name: selectedFile.name, status: 'done' } ] : []}
            >
              <Button icon={<UploadOutlined />}>Select PDF File</Button>
            </Upload>
            <Space style={{ marginTop: 16 }}>
              <Button
                icon={<BugOutlined />}
                onClick={() => setShowDebugger(true)}
                title="Debug lab import issues"
              >
                Debug
              </Button>
              <Button type="primary" onClick={handleProcessPdf} disabled={!selectedFile || !labCollectionDate}>
                Process PDF for Review
              </Button>
            </Space>
          </Space>
        ) : (
          <div>
            <Title level={5}>Review Extracted Data</Title>
            {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
            
            <Row gutter={16} style={{ marginBottom: 24 }}>
              <Col span={12}>
                <Descriptions title="Selected Patient (System)" bordered column={1} size="small">
                  <Descriptions.Item label="Name">{formatName(selectedPatientDemographics?.lastName, selectedPatientDemographics?.firstName, selectedPatientDemographics?.middleName)}</Descriptions.Item>
                  <Descriptions.Item label="DOB">{selectedPatientDemographics?.dob ? moment(selectedPatientDemographics.dob).format('YYYY-MM-DD') : 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Sex">{selectedPatientDemographics?.sex || 'N/A'}</Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={12}>
                <Descriptions title="Data from PDF" bordered column={1} size="small">
                  <Descriptions.Item label="Name">{extractedDemographics?.rawNameString || formatName(extractedDemographics?.lastName, extractedDemographics?.firstName, extractedDemographics?.middleName)}</Descriptions.Item>
                  <Descriptions.Item label="DOB">{extractedDemographics?.dob || 'N/A'}</Descriptions.Item>
                  <Descriptions.Item label="Sex">{extractedDemographics?.sex || 'N/A'}</Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <Text strong>
                Parsed Lab Results ({selectedTests.size} of {parsedLabData.length} selected):
              </Text>
              <Space>
                <Button
                  size="small"
                  onClick={() => handleSelectAllGlobal(true)}
                  disabled={isAllSelected}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={() => handleSelectAllGlobal(false)}
                  disabled={selectedTests.size === 0}
                >
                  Deselect All
                </Button>
              </Space>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 8 }}>
              <Space>
                <Text>Show:</Text>
                <Select
                  value={pageSize}
                  onChange={setPageSize}
                  size="small"
                  style={{ width: 100 }}
                >
                  <Select.Option value={10}>10 / page</Select.Option>
                  <Select.Option value={20}>20 / page</Select.Option>
                  <Select.Option value={50}>50 / page</Select.Option>
                  <Select.Option value={100}>100 / page</Select.Option>
                </Select>
              </Space>
            </div>
            <Table
              columns={reviewTableColumns}
              dataSource={parsedLabData}
              rowKey={(record, index) => generateTestId(record)}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: false, // We have our own size changer
                showQuickJumper: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
              }}
              scroll={{ x: 'max-content', y: 300 }}
              size="small"
            />
            <Space style={{ marginTop: 24, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setReviewStep(false)}>Back to Upload</Button>
              <Button
                type="primary"
                onClick={handleSaveImportedLabs}
                disabled={selectedTests.size === 0}
              >
                Confirm and Save {selectedTests.size} Selected Test{selectedTests.size !== 1 ? 's' : ''}
              </Button>
            </Space>
          </div>
        )}
         {error && !reviewStep && <Alert message={error} type="error" showIcon style={{ marginTop: 16 }} />}
      </Spin>

      {/* Debug Modal */}
      <LabImportDebugger
        visible={showDebugger}
        onClose={() => setShowDebugger(false)}
        patientId={patientId}
      />
    </Modal>
  );
};

export default LabPdfImportModal;
